export type ContentKey = string | readonly [string];

export type RequiredContentKey<Key extends ContentKey> = Key extends string ? Key : never;
export type OptionalContentKey<Key extends ContentKey> = Key extends readonly [string] ? Key[0] : never;
export type PlainContentKeys<Key extends ContentKey> = RequiredContentKey<Key> | OptionalContentKey<Key>;

export type ConcatenatedKeys<Prefixes extends string, Keys extends ContentKey> = Keys extends string ? `${Prefixes}.${Keys}` : Prefixes extends any ? [`${Prefixes}.${Keys[0]}`] : never;

export type KeyedEntity<Keys extends ContentKey, Entity> = ({ [Key in RequiredContentKey<Keys>]: Entity } & { [Key in OptionalContentKey<Keys>]?: Entity }) extends infer T ? { [Key in keyof T]: T[Key] } : never;
export type PlainKeyedEntity<Keys extends ContentKey, Entity> = { [Key in PlainContentKeys<Keys>]: Entity };

export enum ContentType
{
    Text,
    Html
}

export interface ContentItem
{
    data: string;
    type: ContentType;
}

export type Content<Keys extends ContentKey> = KeyedEntity<Keys, ContentItem>;

export enum DBContentMode
{
    Image = "Image",
    Html = "Html",
    Text = "Text",
}

export const ContentTypeMap: Record<ContentType, DBContentMode> = {
    [ContentType.Text]: DBContentMode.Text,
    [ContentType.Html]: DBContentMode.Html,
};

export interface AbstractDBContent<Mode extends DBContentMode, DateType = string>
{
    createdAt: DateType;
    data: string | null;
    id: string;
    key?: string | null;
    mode?: Mode | null;
    order: number;
    updatedAt: DateType;
}

export interface ContentSynchronizeOptions
{
    truncateFirst?: boolean;
}

interface ContentSynchronizeParameters<Keys extends readonly ContentKey[]> extends ContentSynchronizeOptions
{
    keys: Keys;
    newContent: Content<Keys[number]>;
    oldContent: AbstractDBContent<any>[];
    onCreate: (key: string, item: ContentItem, order: number) => Promise<void>;
    onChange: (id: string, item: ContentItem, order: number) => Promise<void>;
    onDelete?: (id: string) => Promise<void>;
}

export class ContentService
{
    public static get<Mode extends DBContentMode, DBContent extends AbstractDBContent<Mode>[], Keys extends readonly ContentKey[]>(dbContent: DBContent, keys: Keys, onWarn?: (message: string) => void): Content<Keys[number]>
    {
        const result = {} as Content<Keys[number]>;

        for (const key of keys)
        {
            const keyValue = typeof key === "string" ? key : key[0];
            const item = dbContent.find(item => item.key === keyValue);
            if (item !== undefined)
            {
                const { data, mode } = item;
                const type = this.modeToType(mode);
                if (type !== undefined)
                {
                    this.setItem(result, key, { data: data ?? "", type });
                }
                else 
                {
                    onWarn?.(`Unsupported mode "${mode}" for key "${typeof key === "string" ? key : `[${key[0]}]`}"`);
                }
            }
            else if (typeof key === "string")
            {
                onWarn?.(`Key "${typeof key === "string" ? key : `[${key[0]}]`}" not found`);
            }
        }

        return result;
    }

    public static getJS<Mode extends DBContentMode, DBContent extends AbstractDBContent<Mode>[], Keys extends readonly ContentKey[]>(dbContent: DBContent, keys: Keys): string
    {
        const typeMap: Record<ContentType, string> = {
            [ContentType.Text]: "Api.ContentType.Text",
            [ContentType.Html]: "Api.ContentType.Html",
        }

        let result = "const pageContent: Api.Content<typeof PageKeys[number]> = {\r\n";

        const appendItem = (key: string, item: AbstractDBContent<Mode>) =>
        {
            const type = this.modeToType(item.mode);
            const typeText = type === undefined ? "undefined" : typeMap[type];
            const dataText = item.data === null ? `""` : item.data.includes("\r") || item.data.includes("\n") ? "`" + item.data + "`" : `"${item.data.replaceAll("\"", "\\\"",)}"`;
            result += `    "${key}": {\r\n        data: ${dataText},\r\n        type: ${typeText},\r\n    },\r\n`;
        }

        const keyValues = keys.map(key => typeof key === "string" ? key : key[0]);

        for (const key of keyValues)
        {
            const item = dbContent.find(item => item.key === key);
            if (item !== undefined)
            {
                appendItem(key, item);
            }
        }

        for (const item of dbContent)
        {
            const key = item.key;
            if (key === null || key === undefined || !keyValues.includes(key))
            {
                appendItem(key ?? "", item);
            }
        }

        result += "};\r\n";

        return result;
    }

    public static getPlainKey<Key extends ContentKey>(key: Key): PlainContentKeys<Key>
    {
        return (typeof key === "string" ? key : key[0]) as PlainContentKeys<Key>;
    }

    public static getItem<Key extends ContentKey, I>(items: KeyedEntity<Key, I>, key: Key): Key extends string ? I : I | undefined
    {
        const keyValue = this.getPlainKey(key);
        return (items as any)[keyValue];
    }

    public static keyExists<Keys extends readonly ContentKey[], Key extends ContentKey>(key: Key, keys: Keys): boolean
    {
        const keyString = typeof key === "string" ? key : key[0];
        return keys.find(k => typeof k === "string" ? k === keyString : k[0] === keyString) !== undefined;
    }

    public static setItem<Key extends ContentKey, I>(items: KeyedEntity<Key, I>, key: Key, item: I): void
    {
        const keyValue = this.getPlainKey(key);
        (items as any)[keyValue] = item;
    }

    public static async synchronize<Keys extends readonly ContentKey[]>({ keys, newContent, oldContent, onChange, onCreate, onDelete, truncateFirst }: ContentSynchronizeParameters<Keys>): Promise<void>
    {
        oldContent = [...oldContent];

        if (truncateFirst && onDelete !== undefined)
        {
            for (const item of oldContent)
            {
                console.log(`Deleting content record id = ${item.id}...`);
                await onDelete(item.id);
            }
            oldContent = [];
        }

        let order = 0;
        for (const key of keys)
        {
            const item = this.getItem(newContent, key);
            if (item !== undefined)
            {
                const keyValue = typeof key === "string" ? key : key[0];
                const oldItemIndex = oldContent.findIndex(item => item.key === keyValue);
                if (oldItemIndex >= 0)
                {
                    const oldItem = oldContent[oldItemIndex];
                    if (oldItem.data !== item.data || oldItem.mode !== ContentTypeMap[item.type] || oldItem.order !== order)
                    {
                        console.log(`Synchronizing key ${keyValue}...`);
                        await onChange(oldItem.id, item, order);
                    }
                    oldContent.splice(oldItemIndex, 1);
                }
                else
                {
                    console.log(`Creating key ${keyValue}...`);
                    await onCreate(keyValue, item, order);
                }
            }
            order++;
        }

        if (onDelete !== undefined)
        {
            for (const item of oldContent)
            {
                console.log(`Deleting content record id = ${item.id}...`);
                await onDelete(item.id);
            }
        }
    }

    public static validate<Keys extends readonly ContentKey[]>(content: AbstractDBContent<any>[], keys: Keys, options?: { allowExceptions?: boolean; slug?: string }): void | never
    {
        const check = (condition: boolean, message: string): void | never =>
        {
            if (!condition)
            {
                message = `Content [${options?.slug}] error: ${message}`;
                if (options?.allowExceptions)
                {
                    throw new Error(message);
                }
                console.error(message);
            }
        }

        for (const item of content)
        {
            const key = item.key;
            //check(key !== undefined && key !== null, `Key is undefined for id = [${item.id}]`);
            //check(this.keyExists(key!, keys), `Key [${key}] is not defined within key list`);
        }

        for (const key of keys)
        {
            if (typeof key === "string")
            {
                check(content.find(item => item.key === key) !== undefined, `Required key ${key} not found`);
            }
        }
    }

    private static modeToType<Mode extends DBContentMode>(mode: Mode | null | undefined): ContentType | undefined
    {
        const modeLowerCase = mode?.toLowerCase();
        return modeLowerCase === undefined || modeLowerCase == DBContentMode.Text.toLowerCase() ? ContentType.Text : modeLowerCase === DBContentMode.Html.toLowerCase() ? ContentType.Html : undefined;
    }
}